import React from 'react'
import { observer } from 'mobx-react-lite'

import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { InstitutionLogo, MerchantLogo, Text } from '@mxenabled/mxui'

import { Drawer, useGlobalCopyStore } from '../../../../common'
import { type RepeatingTransaction, RepeatingType } from '../../../../core'

import { getFrequencyLabel } from '../../../../core/utils/RecurringTransactionsUtil'
import { RecurringTransactionDetails } from '../../RecurringTransactionDetails'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

interface RecurringRowProps {
  onClick?: (repeatingTransaction: RepeatingTransaction) => void
  repeatingTransaction: RepeatingTransaction
}

export const RecurringRow = observer(
  ({ onClick = () => {}, repeatingTransaction }: RecurringRowProps) => {
    const { recurring: copy } = useGlobalCopyStore()

    const [isDetialsOpen, setIsDetailsOpen] = React.useState(false)

    const categoryGuid = React.useMemo(
      () =>
        repeatingTransaction.transactions.length === 0
          ? ''
          : repeatingTransaction.transactions[0].category_guid || '',
      [repeatingTransaction],
    )

    const institutionName = repeatingTransaction.account?.displayName || ''

    const isCredit = repeatingTransaction.repeating_transaction_type === RepeatingType.Income
    const statusLabel = getFrequencyLabel(copy, repeatingTransaction, true)

    const onItemClick = () => {
      onClick(repeatingTransaction)
      setIsDetailsOpen(true)
    }

    return (
      <ListItem disableGutters={true} disablePadding={true}>
        <ListItemButton onClick={onItemClick}>
          <ListItemAvatar>
            <MerchantLogo
              categoryGuid={categoryGuid}
              merchantGuid={repeatingTransaction.merchant_guid || ''}
            />
          </ListItemAvatar>
          <ListItemText>
            <Stack direction="row" gap={4} sx={{ justifyContent: 'space-between' }}>
              <Stack direction="column" sx={{ width: '50%' }}>
                <Text bold={true} variant="body1">
                  {repeatingTransaction.description}
                </Text>
                <Stack alignItems="center" direction="row" gap={6}>
                  {repeatingTransaction.account?.institution_guid && (
                    <InstitutionLogo
                      alt={institutionName}
                      institutionGuid={repeatingTransaction.account?.institution_guid}
                      size={12}
                    />
                  )}
                  <Text variant="caption">{institutionName}</Text>
                </Stack>
              </Stack>

              <Stack alignItems="flex-end" sx={{ width: '50%' }}>
                <Text
                  bold={true}
                  color={isCredit ? 'success.main' : 'text.primary'}
                  variant="body1"
                >
                  {isCredit ? '+' : ''}
                  {formatCurrency(repeatingTransaction.amount, '0,0.00')}
                </Text>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 6,
                    justifyContent: 'flex-end',
                    textTransform: 'uppercase',
                    width: '100%',
                  }}
                >
                  <Text bold={true} color="text.secondary" variant="caption">
                    {statusLabel}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
          </ListItemText>
        </ListItemButton>

        <Drawer
          ariaLabelClose={copy.close_drawer_aria}
          isOpen={isDetialsOpen}
          onClose={() => setIsDetailsOpen(false)}
          title={
            repeatingTransaction.repeating_transaction_type === RepeatingType.Income
              ? copy.income_details
              : copy.expense_details
          }
        >
          <RecurringTransactionDetails
            onDeleted={() => setIsDetailsOpen(false)}
            recurringTransaction={repeatingTransaction}
            selectedRecurrence={null}
          />
        </Drawer>
      </ListItem>
    )
  },
)
